// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { apiClient } from './client';
import type { AuthUser } from '@/store/authStore';

export interface LoginRequest {
  readonly firmCode: string;
  readonly companyCode?: string;
  readonly email: string;
  readonly password: string;
  readonly otpCode?: string;
}

export interface LoginResponse {
  readonly token: string;
  readonly user: AuthUser;
  readonly expiresAt: string;
  // 2FA acik kullanicilarda ilk adim: token gelmez, dogrulama kodu istenir
  readonly twoFactorRequired?: boolean;
  readonly channel?: string;
}

export interface CompanyInfo {
  readonly code: string;
  readonly displayName: string;
}

// Header firma seciciye kaynak — merkezi kimlik defteri
export interface AccessibleCompany {
  readonly firmCode: string;
  readonly displayName: string;
  readonly isDefault: boolean;
  readonly isCurrent: boolean;
  readonly lastAccessedAt?: string | null;
}

export interface RegisterRequest {
  readonly firmCode: string;
  readonly companyCode?: string;
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
  readonly role?: string;
}

export interface RegisterResponse {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
}

export interface ChangePasswordRequest {
  readonly currentPassword: string;
  readonly newPassword: string;
}

export const authService = {
  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/auth/login', request);
    return response.data;
  },

  async listCompanies(firmCode: string, email?: string): Promise<CompanyInfo[]> {
    const response = await apiClient.post<{ companies: CompanyInfo[] }>('/api/auth/companies', {
      firmCode,
      email,
    });
    return response.data.companies;
  },

  async me(): Promise<AuthUser> {
    const response = await apiClient.get<AuthUser>('/api/auth/me');
    return response.data;
  },

  async register(request: RegisterRequest): Promise<RegisterResponse> {
    const response = await apiClient.post<RegisterResponse>('/api/auth/register', request);
    return response.data;
  },

  async changePassword(request: ChangePasswordRequest): Promise<void> {
    await apiClient.post('/api/auth/change-password', request);
  },

  // Merkezi kimlik — kullanicinin erisebildigi firmalar (header firma secici)
  async myCompanies(): Promise<AccessibleCompany[]> {
    const response = await apiClient.get<{ companies: AccessibleCompany[] }>('/api/me/companies');
    return response.data.companies;
  },

  // Aktif firmayi degistir — yeni token doner (re-mint)
  async switchActiveCompany(firmCode: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(`/api/me/active-company/${encodeURIComponent(firmCode)}`);
    return response.data;
  },

  // 2FA (opsiyonel) ac/kapa
  async setTwoFactor(enabled: boolean, channel?: string): Promise<void> {
    await apiClient.post('/api/me/two-factor', { enabled, channel });
  },

  // Kullanicinin merkezi kimlik guvenlik/iletisim durumu (ayar ekrani)
  async getSecurity(): Promise<SecurityInfo> {
    const response = await apiClient.get<SecurityInfo>('/api/me/security');
    return response.data;
  },
};

export interface SecurityInfo {
  readonly hasIdentity: boolean;
  readonly twoFactorEnabled: boolean;
  readonly channel?: string | null;
  readonly phone?: string | null;
  readonly whatsapp?: string | null;
  readonly phoneVerified?: boolean;
  readonly emailVerified?: boolean;
  readonly marketingConsent?: boolean;
}
